import { Injectable, signal } from '@angular/core';
import { components } from '../../schema/backend';
import { AiResponse } from '../../schema/frontendTypes';
import { USDMWorkflow, WorkflowUpdate } from '../workflow.service';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { FileSystemService } from '../file-system.service';
import { WorkspaceTabsService } from '../workspace-tabs.service';

type UsdmWorkflowState = components['schemas']['UsdmWorkflowState-Output'];
export type workflowStatus = {
  [stateName: string]: {
    updates: WorkflowUpdate[],
    status: 'pending' | 'success' | 'error' | 'current'
  }
}

@Injectable({
  providedIn: 'root',
})
export class UsdmService {
  appState: UsdmWorkflowState | null = null;
  workflowUpdate = signal<WorkflowUpdate | null>(null);
  workflowStatus = signal<workflowStatus>({});

  constructor(
    private http: HttpClient,
    private fs: FileSystemService,
    private wsTabs: WorkspaceTabsService
  ) {
    this.wsTabs.tabList.update((tabs) => {
      if (tabs.find(t => t.label === 'USDM')) return tabs;
      tabs.push({ label: 'USDM' });
      return tabs;
    });
    this.wsTabs.activeTab.update(() => this.wsTabs.tabList()[this.wsTabs.tabList().length - 1]);
  }

  async handleAiAnswer(aiAnswer: AiResponse, workflow: USDMWorkflow): Promise<{ nextPrompt: string | null, setNewContext: boolean }> {

    let nextPrompt: string | null = null;
    let setNewContext: boolean = false;

    if (!workflow.data.protocolFilePath) {
      throw new Error('Protocol file path is required for USDM workflow', { cause: { code: "MISSING_PROTOCOL_PATH" } });
    }
    if (!this.appState) {
      this.appState = {
        study_uid: workflow.data.osbStudyUid || null,
        protocol_path: workflow.data.protocolFilePath,
        recent_ai_answer: aiAnswer.content,
        current_state: "start",
        soa_tables: [],
        soa_footnotes: "",
        visits: [],
        osb_server_url: workflow.data.osbServerUrl || '',
        states_to_run: workflow.data.osbStates || [],
        failed_states: [],
      };
      this.workflowStatus.set({});
    }
    this.appState.recent_ai_answer = aiAnswer.content;
    // const newState: UsdmWorkflowState = await lastValueFrom(
    //   this.http.post<UsdmWorkflowState>(environment.backendUrl + '/usdm/next', { ...this.appState })
    // );
    const stream = await this.stream('/usdm/next', { ...this.appState });
    let newState: UsdmWorkflowState | null = null;
    for await (const update of stream) {
      this.updateWorkflowStatus(update);
      if ('current_state' in update) {
        newState = update as UsdmWorkflowState;
        break;
      } else if ('message' in update) {
        this.workflowUpdate.set(update as WorkflowUpdate);
      }
    }
    if (!newState) {
      throw new Error('No state received from SDTM workflow', { cause: { code: "NO_STATE_UPDATE_RECEIVED_USDM" } });
    }
    if (newState.error) {
      const err = newState.error;
      newState.error = null;
      this.appState = newState;
      console.error(err);
      // throw new Error(err, { cause: { code: "USDM_WORKFLOW_ERROR" } });
      return { nextPrompt: "SKIP", setNewContext: true }
    }
    if (!newState.next_state_prompt && newState.current_state !== 'done') {
      this.appState = newState;
      return { nextPrompt: "SKIP", setNewContext: true };
      // throw new Error('No next state prompt found in SDTM workflow state', { cause: { code: "CRITICAL_PROMPT_NOT_FOUND" } });
    }
    if (newState.current_state !== this.appState.current_state) {
      // :: NEW STATE ::
      setNewContext = true;
      if (newState.current_state.split("/")[0] !== this.appState.current_state.split("/")[0]) {
        if ((newState.current_state.startsWith("SOA") && !this.appState.pdf_content) || newState.current_state.startsWith("Criter")) {
          const pdfBlob = await this.fs.rootNode.getNode(workflow.data.protocolFilePath)?.getBlob();
          if (!pdfBlob) {
            throw new Error('Protocol file not found', { cause: { code: "PROTOCOL_FILE_NOT_FOUND" } });
          }
          // convert to base64 string
          const base64String = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(pdfBlob);
          });
          newState.pdf_content = base64String
        }
      }
      this.appState = newState;
    }

    // :: STATE UPDATED::
    if (this.appState.soa_tables && !environment.production) {
      for (let index = 0; index < this.appState.soa_tables.length; index++) {
        const table = this.appState.soa_tables[index];
        const soaFolder = this.fs.rootNode.insert(`/workspace/soa_tables`, true, null);
        await this.fs.uploadFiles([new File([table], `${index}.csv`, { type: "text/csv" })], soaFolder);
        await this.fs.refreshRootNode();
      }
    }
    nextPrompt = newState.next_state_prompt as string;
    if (this.appState.current_state === 'done') {
      nextPrompt = null;
      await this.fs.uploadFiles([new File([JSON.stringify(this.appState.usdm!)], (this.appState.protocol_path.split('/').pop()?.replace('.pdf', '') || 'usdm') + '.json', { type: "application/json" })], this.fs.rootNode);
      this.fs.refreshRootNode();
    }
    //  else if (this.appState.next_state_prompt === 'SKIP') {
    //   // hack to move to next state
    //   // console.warn("soa/complete. THIS SHOULD NOT HAPPEN");
    //   const result = await this.handleAiAnswer(aiAnswer, workflow);
    //   this.appState = result.appState;
    //   return { nextPrompt: result.nextPrompt, setNewContext: result.setNewContext, appState: this.appState };
    // }
    return { nextPrompt, setNewContext };
  }

  async skipCurrentState(path: string) {
    const newState = await lastValueFrom(this.http.post<UsdmWorkflowState>(environment.backendUrl + '/usdm/skip', { ...this.appState }));
    if ((newState.current_state.startsWith("SOA")) || newState.current_state.startsWith("Criter")) {
      const pdfBlob = await this.fs.rootNode.getNode(path)?.getBlob();
      if (!pdfBlob) {
        throw new Error('Protocol file not found', { cause: { code: "PROTOCOL_FILE_NOT_FOUND" } });
      }
      // convert to base64 string
      const base64String = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });
      newState.pdf_content = base64String
    }
    this.appState = newState;
    return { nextPrompt: newState.next_state_prompt, setNewContext: true };
  }

  updateWorkflowStatus(update: UsdmWorkflowState | WorkflowUpdate) {
    const currentStatus = this.workflowStatus();
    const newStatus = { ...currentStatus };
    if ('message' in update) {
      const currState = this.appState!.current_state;
      if (!newStatus[currState]) {
        newStatus[currState] = { updates: [], status: 'current' };
      }
      newStatus[currState] = {
        ...newStatus[currState],
        updates: [...newStatus[currState].updates, update as WorkflowUpdate],
        status: 'current',
      };
    } else {
      const newAppState = { ...update };

      const statesToRun = this.getStatesToRun();
      if (newAppState.current_state === "CreateStudy" && !statesToRun.includes("CreateStudy")) {
        statesToRun.unshift("CreateStudy");
      }
      statesToRun.push("done");

      const currentStateIdx = statesToRun.indexOf(newAppState.current_state);
      for (let i = 0; i < currentStateIdx; i++) {
        const stateName = statesToRun[i];
        if (!newStatus[stateName]) {
          newStatus[stateName] = { updates: [], status: newAppState.failed_states.includes(stateName) ? 'error' : 'success' };
        } else {
          newStatus[stateName].status = newAppState.failed_states.includes(stateName) ? 'error' : 'success';
        }
      }
      newStatus[newAppState.current_state] = {
        ...(newStatus[newAppState.current_state] || { updates: [] }),
        status: newAppState.current_state === 'done' ? 'success' : 'current'
      };
      for (let i = currentStateIdx + 1; i < statesToRun.length; i++) {
        const stateName = statesToRun[i];
        newStatus[stateName] = newStatus[stateName] || { updates: [], status: 'pending' };
      }
    }
    // console.warn("Workflow Status Update:", newStatus);
    this.workflowStatus.update(() => newStatus);
  }

  getStatesToRun() {
    if (this.appState) {
      const mapping = {
        "Properties": "HighLevelDesign",
        "Arms": "Arm",
        "Population": "Population",
        "Debug": "Debug",
        "Debug2": "Debug2",
        "Debug3": "Debug3",
        "Objectives": "Objectives",
        "Endpoints": "Endpoints",
        "Elements": "StudyElement",
        "Inclusion/Exclusion Criteria": "Criteria",
        "SOA Tables": "SOA",
        "Epochs & Visits": "EpochVisit",
        "Activities": "Activities",
        "Schedule Of Activities": "ScheduleOfActivities",
        "Download USDM": "DownloadUsdm",
      } as { [key: string]: string };
      const states = this.appState.states_to_run.map(s => mapping[s.name]);
      const curState = this.appState.current_state;
      if (curState === 'CreateStudy') {
        states.unshift('CreateStudy');
      }
      states.push('done');
      return states;
    } else {
      return [];
    }
  }

  async stream(endpoint: string, data: UsdmWorkflowState): Promise<AsyncIterable<UsdmWorkflowState | WorkflowUpdate>> {
    const token = sessionStorage.getItem('userAccessToken')
    const stream = {
      async *[Symbol.asyncIterator]() {
        const url = `${environment.backendUrl}${endpoint}`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token || ''}`,
          },
          body: JSON.stringify(data),
        });

        if (!response.body) {
          throw new Error('Response body is null');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = ''; // Buffer to accumulate incomplete chunks

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const text = decoder.decode(value, { stream: !done });
            buffer += text;

            const lines = buffer.split('\n');

            // Keep the last line in the buffer (it might be incomplete)
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.trim()) {
                try {
                  const json = JSON.parse(line);
                  yield json;
                } catch (e) {
                  // If a complete line (separated by \n) fails to parse, it's likely corrupted data
                  console.error('Failed to parse complete JSON line:', line.substring(0, 100), e);
                }
              }
            }
          }

          // Process any remaining data in the buffer
          if (buffer.trim()) {
            try {
              const json = JSON.parse(buffer);
              yield json;
            } catch (e) {
              console.error('Failed to parse final buffer:', buffer.substring(0, 100), e);
            }
          }
        } finally {
          reader.releaseLock();
        }
      },
    };

    return stream;
  }

  cleanup() {
    this.appState = null;
    this.workflowUpdate.set(null);
  }
}
